import { FunctionComponent, useMemo } from "react";
import { Menu, MenuProps } from "antd";
import { useNavigate } from "react-router-dom";
import { NavGroup, NavItem } from "./navigationConfig";

interface SidebarMenuProps {
    groups: NavGroup[];
    onNavigate?: () => void;
}

const toMenuItems = (items: NavItem[]): NonNullable<MenuProps["items"]> =>
    items.map((item) =>
        item.children
            ? { key: item.key, icon: item.icon, label: item.label, children: toMenuItems(item.children) }
            : { key: item.key, icon: item.icon, label: item.label }
    );

const collectTargets = (items: NavItem[], map: Map<string, NavItem>) => {
    items.forEach((item) => {
        if (item.path) map.set(item.key, item);
        if (item.children) collectTargets(item.children, map);
    });
};

export const SidebarMenu: FunctionComponent<SidebarMenuProps> = ({ groups, onNavigate }) => {
    const navigate = useNavigate();

    const { menuItems, targets } = useMemo(() => {
        const items: NonNullable<MenuProps["items"]> = [];
        const map = new Map<string, NavItem>();
        groups.forEach((group, index) => {
            if (group.label) {
                items.push({ type: "group", key: `group-${index}-${group.label}`, label: group.label, children: toMenuItems(group.items) });
            } else {
                items.push(...toMenuItems(group.items));
            }
            collectTargets(group.items, map);
        });
        return { menuItems: items, targets: map };
    }, [groups]);

    const handleClick: MenuProps["onClick"] = ({ key }) => {
        const target = targets.get(key);
        if (target?.path) {
            navigate(target.path, target.state ? { state: target.state } : undefined);
            onNavigate?.();
        }
    };

    return <Menu mode="inline" theme="dark" items={menuItems} onClick={handleClick} />;
};
